// Imports
import React, { useRef, useState, useContext, useEffect, useCallback } from 'react';
import uniqueId from 'lodash/uniqueId';
import { ThemeProvider, createGlobalStyle } from 'styled-components/macro';
import FieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/field-container';
import { Label } from '../../../../../../shareables/foundation/front-end/components/forms/labels';
import ControlGroup from '../../../../../../shareables/foundation/front-end/components/forms/control-group';
import FieldErrorMessage from '../../../../../../shareables/foundation/front-end/components/forms/field-error-message';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import CKEditorBuild from '@sparksuite/ckeditor-build';
import useUpdateOnlyEffect from '../../../../../../shareables/foundation/front-end/utils/use-update-only-effect';
import saveValue from './save-value';
import APIConnectedFormContext, { ContextDetails } from '../../../../../contexts/api-connected-form';
import debounce from 'lodash/debounce';
import { z } from 'zod';
import parsePersistentFormValue from './utils/parse-persistent-form-value';
import BaseFieldProps from './base-field-props';


// Define a global style for hiding the header toggle
//  -> It allows a user to add header columns and 2+ header rows, neither of which are supported by Markdown syntax
const HideTableHeaderSettingsForMarkdownMode = createGlobalStyle`
	.ck-toolbar[aria-label="Table toolbar"] {
		.ck-dropdown {
			.ck-list {
				.ck-list__item:first-child {
					display: none !important;
				}
				
				.ck-list__separator:nth-child(2) {
					display: none !important;
				}
			}
		}
	}
`;


// Build editor configurations
const fullConfig = {
	removePlugins: ['Markdown', 'RestrictTableCellContent', 'NormalizeTableHeader'],
	toolbar: {
		items: [
			'heading',
			'|',
			'bold',
			'italic',
			'underline',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'alignment',
			'|',
			'link',
			'bulletedList',
			'numberedList',
			'blockQuote',
			'horizontalLine',
			'|',
			'outdent',
			'indent',
			'|',
			'uploadImage',
			'insertTable',
			'mediaEmbed',
			'htmlEmbed',
			'|',
			'sourceEditing',
			'|',
			'undo',
			'redo',
		],
	},
	link: {
		decorators: {
			openInNewTab: {
				mode: 'manual',
				label: 'Open in a new tab',
				attributes: {
					target: '_blank',
					rel: 'noopener noreferrer',
				},
			},
		},
	},
	image: {
		toolbar: [
			'imageStyle:inline',
			'imageStyle:wrapText',
			'imageStyle:breakText',
			'|',
			'toggleImageCaption',
			'imageTextAlternative',
		],
	},
	table: {
		contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
		defaultHeadings: {
			rows: 1,
			columns: 0,
		},
		tableProperties: {
			defaultProperties: {
				borderStyle: 'double',
				borderColor: 'hsl(0, 0%, 70%)',
				borderWidth: '0.0625rem',
				width: '100%',
				height: '100%',
			},
		},
		tableCellProperties: {
			defaultProperties: {
				borderStyle: 'solid',
				borderColor: 'hsl(0, 0%, 75%)',
				borderWidth: '0.0625rem',
			},
		},
	},
	htmlSupport: {
		allow: [
			{
				name: 'iframe',
				attributes: true,
				classes: true,
				styles: true,
			},
			{
				name: /^(strong|i|b|u|em|s|blockquote|p|h1|h2|h3|h4|h5|h6|img|figure|figcaption|a|ul|ol|li|hr|table|thead|tbody|tr|th|td|div|span)$/,
				attributes: ['id'],
				classes: true,
				styles: true,
			},
		],
	},
	fontFamily: {
		options: [
			'default',
			'Arial, Helvetica, sans-serif',
			'Arial Black, Arial Bold, sans-serif',
			'Book Antiqua, Times, Times New Roman, serif',
			'Comic Sans MS, sans-serif',
			'Courier New, Courier, monospace',
			'Georgia, Times, Times New Roman, serif',
			'Helvetica, Helvetica Neue, Arial, sans-serif',
			'Impact, Arial Narrow Bold, sans-serif',
			'Tahoma, Geneva, Verdana, sans-serif',
			'Terminal, Courier New, monospace',
			'Times New Roman, Times, serif',
			'Trebuchet MS, Tahoma, Arial, sans-serif',
			'Verdana, Geneva, Tahoma, sans-serif',
		],
	},
	licenseKey: 'GPL',
	mediaEmbed: {
		previewsInData: true,
	},
};

const markdownConfig = {
	removePlugins: [
		'ImageCaption',
		'ImageToolbar',
		'ImageStyle',
		'Indent',
		'MediaEmbed',
		'SourceEditing',
		'HtmlEmbed',
		'Alignment',
		'FontSize',
		'FontFamily',
		'FontColor',
		'FontBackgroundColor',
		'ImageResize',
		'MediaEmbedToolbar',
		'TableProperties',
		'TableCellProperties',
		'GeneralHtmlSupport',
		'Underline',
	],
	toolbar: {
		items: [
			'heading',
			'|',
			'bold',
			'italic',
			'|',
			'link',
			'bulletedList',
			'numberedList',
			'blockQuote',
			'horizontalLine',
			'uploadImage',
			'insertTable',
			'|',
			'undo',
			'redo',
		],
	},
	table: {
		contentToolbar: ['tableColumn', 'tableRow'],
		defaultHeadings: {
			rows: 1,
			columns: 0,
		},
	},
	licenseKey: 'GPL',
};


// Define the accepted props
interface Props extends BaseFieldProps {
	/** A function that handles file uploads coming from the `CKEditor` interface. The function is passed the file to be uploaded as its parameter. */
	handleUpload: (file: File) => Promise<string>;
	
	
	/** The initial value state of the field. Will be ignored on subsequent renders. */
	initialValue?: string;
	
	
	/** Whether the field's getter should convert the field value to Markdown. Limits the formatting controls to those supported by Markdown. Defaults to `false`. */
	outputMarkdown?: boolean;
	
	
	/** A function that handles a change in value to the field. The function is passed the latest value as its parameter. */
	onChange?: (newValue: string) => void;
	
	
	/** A function that provides the getter for the field to be bound, usually to a `ref`. */
	bindGetter?: (getter: () => string) => void;
	
	
	/** A function that provides the setter for the field to be bound, usually to a `ref`. */
	bindSetter?: (setter: (value: string) => void) => void;
	
	
	/** Whether to automatically focus this field on the initial render. Defaults to `false`. */
	autoFocus?: boolean;
}

type CompleteProps = Props &
	Pick<
		React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
		'required' | 'maxLength' | 'minLength'
	>;


// Function components
const Editor: React.FC<CompleteProps> = (props) => {
	// Break off the props we want to use
	const {
		name,
		handleUpload,
		label,
		note,
		initialValue,
		outputMarkdown,
		errorMessage: errorMessageProp,
		onChange: onChangeProp,
		bindGetter,
		bindSetter,
		recoverState: recoverStateProp,
		exclude,
		autoFocus: autoFocusProp,
		...passThroughProps
	} = props;
	
	
	// Use context
	const context = useContext(APIConnectedFormContext);
	
	
	// Escape the initial value (only when outputting Markdown)
	//  -> These characters should already be escaped, but might not be if something was previously copied-and-pasted
	const escapedInitialValue = outputMarkdown ? initialValue?.replace(/\\*</g, '\\<') : initialValue;
	
	
	// Use state
	const [editor, setEditor] = useState<CKEditorBuild | null>(null);
	const [value, setValue] = useState<string>(escapedInitialValue || '');
	const [constraintValidationError, setConstraintValidationError] = useState('');
	
	
	// Retrieve values from API form's context, falling back to props
	const recoverState = recoverStateProp ?? context?.recoverState;
	const errorMessage = context?.error.field === name ? context.error.message : errorMessageProp;
	
	
	// Bind getter/setter
	bindGetter && bindGetter(() => value);
	bindSetter &&
		bindSetter((value) => {
			editor?.setData(value);
		});
	
	
	// Add and remove getter from connected form
	let addValueGetter: NonNullable<ContextDetails>['addValueGetter'] | null = null;
	let removeValueGetter: NonNullable<ContextDetails>['removeValueGetter'] | null = null;
	
	if (context) {
		addValueGetter = context.addValueGetter;
		removeValueGetter = context.removeValueGetter;
	}
	
	useEffect(() => {
		if (addValueGetter === null || exclude === true) {
			return;
		}
		
		addValueGetter(name, () => value);
		
		return () => {
			if (removeValueGetter === null) {
				return;
			}
			
			removeValueGetter(name);
		};
	}, [name, value, addValueGetter, removeValueGetter, exclude]);
	
	
	// Generate unique ID
	const uniqueID = useRef(uniqueId('editor-field-'));
	
	
	// Handle value changes
	const handleChange = () => {
		// Clear constraint validation error
		setConstraintValidationError('');
		
		
		// Get the editor data
		let data = editor?.getData() ?? '';
		
		
		// Handle replacements for Markdown content
		if (outputMarkdown) {
			data = data.replace(/<figure class="image">/g, '');
			data = data.replace(/<\/figure>/g, '');
			data = data.replace(
				/<(tel:)([^>]+)>/gm,
				(_match: string, tel: string, phoneNumber: string) => `${tel}${encodeURIComponent(phoneNumber)}`
			);
		}
		
		
		// Set the value
		setValue(data);
	};
	
	
	// Handle constraint validation
	const handleConstraintValidation = (e: React.FormEvent<HTMLTextAreaElement>) => {
		e.preventDefault();
		
		setConstraintValidationError(e.currentTarget.validationMessage);
		editor?.editing.view.focus();
	};
	
	
	// Debounce save value function
	const debouncedSaveValue = useCallback(debounce(saveValue, 300), []); // eslint-disable-line react-hooks/exhaustive-deps
	
	
	// Save new value on updates
	useUpdateOnlyEffect(() => {
		recoverState && recoverState.enabled && debouncedSaveValue(name, value, recoverState.uniqueIdentifier);
	}, [value]);
	
	
	// Notify parent of changes
	useUpdateOnlyEffect(() => {
		onChangeProp?.(value);
		context?.onChange?.(true);
	}, [value]);
	
	useUpdateOnlyEffect(() => {
		context?.onExcludeChange?.();
	}, [exclude]);
	
	
	// Use ref
	const hasMounted = useRef(false);
	
	
	// Decide whether to autofocus
	const autoFocus = (window as typeof window & { STORYBOOK?: true }).STORYBOOK ? false : autoFocusProp;
	
	
	// Handle editor initialization
	const handleReady = (editor: CKEditorBuild) => {
		// Store the editor in state
		setEditor(editor);
		
		
		// Focus the editor if necessary
		if (autoFocus) {
			editor.editing.view.focus();
		}
		
		
		// Add source editing event listener
		if (!outputMarkdown) {
			// Get plugin
			const sourceEditing = editor.plugins.get('SourceEditing');
			
			
			// Add event listener
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			sourceEditing.on('change:isSourceEditingMode', (_eventInfo: unknown, _name: string, value: boolean) => {
				if (value) {
					// Get the textarea
					const sourceEditingTextarea = editor.editing.view.getDomRoot()?.nextSibling?.firstChild;
					
					if (!sourceEditingTextarea) {
						throw new Error('This should not be possible');
					}
					
					
					// Add input event listener
					//  -> Must be used because it appears the `change` event is being prevented
					sourceEditingTextarea.addEventListener('input', () => {
						// Trigger the editor's data to update
						sourceEditing.updateEditorData();
					});
				}
			});
		}
		
		
		// Attempt to recover state on mount
		if (recoverState && recoverState.enabled && hasMounted.current === false) {
			hasMounted.current = true;
			
			window.setTimeout(() => {
				const recoveredValue = parsePersistentFormValue(recoverState.uniqueIdentifier, name, z.string());
				
				if (typeof recoveredValue !== 'undefined') {
					editor?.setData(recoveredValue);
				}
			}, 1); // Must happen asynchronously
			
			/* Don't trigger onChange here; it will be triggered before all getters are bound, and cause issues */
		}
	};
	
	
	// Create theme
	const theme = {
		hasError: Boolean(errorMessage || constraintValidationError),
	};
	
	
	// Build config
	const config = {
		...(outputMarkdown ? markdownConfig : fullConfig),
		customUpload: {
			uploadFunction: (file: File) => {
				return new Promise((resolve, reject) => {
					handleUpload(file)
						.then((url) => {
							resolve({
								default: url,
							});
						})
						.catch((reason) => reject(reason));
				});
			},
		},
	};
	
	
	// Return JSX
	return (
		<ThemeProvider theme={theme}>
			{outputMarkdown && <HideTableHeaderSettingsForMarkdownMode />}
			
			<FieldContainer>
				{label && (
					<Label htmlFor={uniqueID.current}>
						{label}
						{note && <small>{note}</small>}
					</Label>
				)}
				
				<ControlGroup>
					<CKEditor
						editor={CKEditorBuild}
						onReady={handleReady}
						onChange={handleChange}
						data={escapedInitialValue}
						config={config}
					/>
					
					<textarea
						value={value}
						aria-hidden={true}
						tabIndex={-1}
						style={{ display: 'none' }}
						onInvalid={handleConstraintValidation}
						{...passThroughProps}
					/>
				</ControlGroup>
				
				{(constraintValidationError || errorMessage) && (
					<FieldErrorMessage message={constraintValidationError || errorMessage || ''} />
				)}
			</FieldContainer>
		</ThemeProvider>
	);
};

Editor.displayName = 'Editor';

export default Editor;
